import type { CSSProperties } from 'react';
import { cx } from '@linaria/atomic';
import { motion, type MotionProps } from 'motion/react';
import type { CommonHTMLAttributes } from '@frontend/types';
import { WeaveThemeValues } from '../../../component-theme/theme-types';
import { mergeClasses } from '../../../helpers/merge-classes';
import { useThemeValues } from '../../../hooks/use-theme-values';
import { useModalContext } from '../provider/modal.provider';
import { modalBackdrop, modalBackdropAdjustedHeight, modalBackdropNonInteractive } from './modal-backdrop.styles';

type ModalBackdropProps = {
  zIndex?: WeaveThemeValues['ZIndex'];
  omitOffset?: boolean;
  showBackdrop?: boolean;
  show?: boolean; // Added to control visibility
};

export const ModalBackdrop = ({
  className,
  onClick,
  zIndex,
  omitOffset,
  show = true,
  style,
  ...rest
}: CommonHTMLAttributes & ModalBackdropProps & MotionProps) => {
  const { loadingState } = useModalContext();
  const { heightOffset } = useThemeValues();
  const applyHeightOffset = !omitOffset && heightOffset > 0;

  /**
   * NOTE: Needs an AnimatedPresence to be in it's component heirarchy.
   * Is done automatically in CenteredModal
   */
  return (
    <motion.div
      className={cx(
        modalBackdrop,
        applyHeightOffset && modalBackdropAdjustedHeight,
        mergeClasses('wv-modal-backdrop', className)
      )}
      style={
        {
          ...style,
          ...(zIndex ? { zIndex } : {}),
          ...(applyHeightOffset ? { '--modal-backdrop-height-offset': `${heightOffset}px` } : {}),
        } as CSSProperties
      }
      initial={{ opacity: 0 }}
      animate={{ opacity: show ? 1 : 0 }}
      exit={{ opacity: 0 }}
      {...rest}
      // prevent click to close while in a loading state
      onClick={
        loadingState?.show
          ? undefined
          : (e) => {
              e.stopPropagation();
              onClick?.(e);
            }
      }
    />
  );
};

export const TrayModalBackdrop = ({
  className,
  onClick,
  zIndex,
  omitOffset,
  showBackdrop = true,
  style,
  ...rest
}: CommonHTMLAttributes & MotionProps & ModalBackdropProps) => {
  const { loadingState } = useModalContext();
  const { heightOffset } = useThemeValues();
  const applyHeightOffset = !omitOffset && heightOffset > 0;
  return (
    <motion.div
      className={cx(
        showBackdrop && modalBackdrop,
        applyHeightOffset && modalBackdropAdjustedHeight,
        // Allows the background to be interactive
        !showBackdrop && modalBackdropNonInteractive,
        mergeClasses('wv-modal-backdrop', className)
      )}
      style={
        {
          ...style,
          ...(zIndex ? { zIndex } : {}),
          ...(applyHeightOffset ? { '--modal-backdrop-height-offset': `${heightOffset}px` } : {}),
        } as CSSProperties
      }
      {...rest}
      // prevent click to close while in a loading state
      onClick={
        loadingState?.show
          ? undefined
          : (e) => {
              e.stopPropagation();
              onClick?.(e);
            }
      }
    />
  );
};
